import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { surgeryService, type SurgicalProcedure, type PostOpPrescription } from "../surgeryService";
import { useClinicSettings } from "../../../hooks/useClinicSettings";
import { parseApiDate } from '../../../lib/format';

const calcAge = (dob?: string) => {
  if (!dob) return null;
  return Math.floor((Date.now() - (parseApiDate(dob) ?? new Date(NaN)).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
};

const fmt = (d?: string) =>
  d ? (parseApiDate(d) ?? new Date(NaN)).toLocaleDateString("es-DO", { day: "2-digit", month: "long", year: "numeric" }) : "-";

const PostOpPrescriptionPrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [proc, setProc] = useState<SurgicalProcedure | null>(null);
  const [prescription, setPrescription] = useState<PostOpPrescription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { clinicName, clinicAddress, logoUrl } = useClinicSettings(proc?.doctor_id);

  useEffect(() => {
    const numericId = Number(id);
    Promise.all([
      surgeryService.getById(numericId),
      surgeryService.getPostOpPrescription(numericId),
    ])
      .then(([procedureData, prescriptionData]) => {
        setProc(procedureData);
        setPrescription(prescriptionData);
      })
      .catch(() => setError("Error cargando la receta."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!proc || !prescription?.items?.length) return;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, [proc, prescription]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "sans-serif" }}>
        <span>Cargando receta...</span>
      </div>
    );
  }

  if (error) {
    return <div style={{ padding: 40, color: "red" }}>{error}</div>;
  }

  if (!proc) {
    return <div style={{ padding: 40, color: "red" }}>Procedimiento no encontrado.</div>;
  }

  const doctor = proc.doctor;
  const patient = proc.patient;
  const age = (patient as any)?.birth_date ? calcAge((patient as any).birth_date) : null;
  const items = prescription?.items ?? [];
  const hasPrescriptions = items.length > 0;

  return (
    <>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; color: #111; background: white; }
        .page { width: 100%; max-width: 148mm; margin: 0 auto; padding: 14mm 16mm; min-height: 210mm; }
        .header { border-bottom: 3px double #111; padding-bottom: 10px; margin-bottom: 14px; }
        .doctor-name { font-size: 16pt; font-weight: bold; }
        .doctor-sub { font-size: 10pt; color: #333; line-height: 1.6; }
        .clinic-sub { font-size: 9pt; color: #555; margin-top: 4px; }
        .meta { display: flex; justify-content: space-between; margin-bottom: 14px; font-size: 10pt; }
        .patient-info { border-bottom: 1px solid #aaa; padding-bottom: 8px; margin-bottom: 8px; }
        .patient-label { font-size: 9pt; color: #555; }
        .patient-name { font-size: 12pt; font-weight: bold; }
        .context-banner { background: #e8f0fe; border: 1px solid #b8cce4; padding: 8px 12px; margin-bottom: 16px; border-radius: 4px; font-size: 10pt; color: #1d4ed8; }
        .rx-symbol { font-size: 28pt; font-weight: bold; color: #1d4ed8; margin-bottom: 16px; line-height: 1; }
        .rx-item { margin-bottom: 18px; page-break-inside: avoid; }
        .rx-num { color: #1d4ed8; font-weight: bold; font-size: 12pt; margin-right: 4px; }
        .rx-name { font-size: 13pt; font-weight: bold; }
        .rx-dose { font-size: 12pt; color: #333; margin-left: 2px; }
        .rx-sig { font-size: 10pt; color: #444; margin-left: 20px; margin-top: 3px; font-style: italic; }
        .rx-instructions { font-size: 10pt; color: #555; margin-left: 20px; margin-top: 2px; }
        .no-rx { text-align: center; color: #888; padding: 30px; font-style: italic; }
        .footer { margin-top: 40px; }
        .signature-line { border-top: 1px solid #111; width: 180px; margin-bottom: 4px; }
        .signature-name { font-size: 11pt; font-weight: bold; }
        .signature-sub { font-size: 9pt; color: #555; }
        @media print {
          @page { margin: 10mm 12mm; size: A5; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ background: "#f0f0f0", padding: "10px 20px", display: "flex", gap: 12, alignItems: "center" }}>
        <button
          onClick={() => window.print()}
          style={{ padding: "6px 16px", background: "#1d4ed8", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
        >
          Imprimir / Guardar PDF
        </button>
        <button
          onClick={() => window.close()}
          style={{ padding: "6px 16px", background: "#6b7280", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}
        >
          Cerrar
        </button>
      </div>

      <div className="page">
        <div className="header">
          {logoUrl && (
            <img src={logoUrl} alt="Logo" style={{ maxWidth: '120px', height: 'auto', marginBottom: 8, printColorAdjust: 'exact' }} />
          )}
          <div className="doctor-name">Dr. {doctor?.first_name} {doctor?.last_name}</div>
          <div className="doctor-sub">
            {doctor?.specialty && <span>{doctor.specialty.name}</span>}
            {(doctor as any)?.license_number && <span> &nbsp;|&nbsp; Exequátur: {(doctor as any).license_number}</span>}
            {(doctor as any)?.phone && <span> &nbsp;|&nbsp; Tel: {(doctor as any).phone}</span>}
          </div>
          {clinicAddress && <div className="clinic-sub">{clinicName} &nbsp;·&nbsp; {clinicAddress}</div>}
          {!clinicAddress && clinicName !== "eCliniq" && <div className="clinic-sub">{clinicName}</div>}
        </div>

        <div className="meta">
          <span>{clinicAddress || "Santo Domingo"}, {fmt(proc.procedure_date)}</span>
        </div>

        <div className="patient-info">
          <div className="patient-label">Paciente</div>
          <div className="patient-name">{patient?.first_name} {patient?.last_name}</div>
          <div style={{ fontSize: "10pt", color: "#444", marginTop: 2 }}>
            {patient?.identification && <span>Cédula: {patient.identification}</span>}
            {age !== null && <span style={{ marginLeft: 12 }}>Edad: {age} años</span>}
          </div>
        </div>

        <div className="context-banner">
          <strong>Receta Post-Operatoria</strong> — {proc.procedure_name}
          {prescription?.generated_at && <span className="ms-3">Generada: {fmt(prescription.generated_at)}</span>}
        </div>

        <div className="rx-symbol">℞</div>

        {!hasPrescriptions ? (
          <div className="no-rx">No hay medicamentos prescritos para este procedimiento.</div>
        ) : (
          items.map((p, i) => (
            <div className="rx-item" key={i}>
              <div>
                <span className="rx-num">{i + 1}.</span>
                <span className="rx-name">{p.medication_name}</span>
                {p.dosage && <span className="rx-dose"> — {p.dosage}</span>}
              </div>
              <div className="rx-sig">
                Sig: {p.route}
                {p.frequency && `, ${p.frequency}`}
                {p.duration && ` — ${p.duration}`}
              </div>
              {p.notes && (
                <div className="rx-instructions">{p.notes}</div>
              )}
            </div>
          ))
        )}

        <div className="footer">
          <div className="signature-line"></div>
          <div className="signature-name">Dr. {doctor?.first_name} {doctor?.last_name}</div>
          {doctor?.specialty && <div className="signature-sub">{doctor.specialty.name}</div>}
          {(doctor as any)?.license_number && (
            <div className="signature-sub">Exequátur: {(doctor as any).license_number}</div>
          )}
        </div>
      </div>
    </>
  );
};

export default PostOpPrescriptionPrint;
